import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Workspace } from '../types';

const API_URL = import.meta.env.VITE_API_URL || '';

interface AuthState {
  token: string | null;
  user: User | null;
  workspace: Workspace | null;
  availableWorkspaces: Workspace[];
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string, workspaceSlug?: string) => Promise<boolean>;
  register: (data: RegisterData) => Promise<boolean>;
  switchWorkspace: (workspaceSlug: string) => Promise<boolean>;
  handleOAuthCallback: (token: string, data?: OAuthCallbackData) => void;
  updateWorkspace: (updates: Partial<Workspace>) => void;
  logout: () => void;
  getAuthHeaders: () => Record<string, string>;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  companyName: string;
  workspaceName: string;
}

interface OAuthCallbackData {
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    avatarUrl?: string;
  };
  workspace?: Workspace;
  workspaces?: Workspace[];
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      workspace: null,
      availableWorkspaces: [],
      isAuthenticated: false,
      isLoading: false,

      login: async (email: string, password: string, workspaceSlug?: string): Promise<boolean> => {
        set({ isLoading: true });
        try {
          const response = await fetch(`${API_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, workspaceSlug }),
          });

          if (!response.ok) {
            set({ isLoading: false });
            return false;
          }

          const data = await response.json();
          set({
            token: data.token,
            user: data.user,
            workspace: data.workspace,
            availableWorkspaces: data.workspaces || [],
            isAuthenticated: true,
            isLoading: false,
          });
          return true;
        } catch (error) {
          console.error('Login error:', error);
          set({ isLoading: false });
          return false;
        }
      },

      register: async (data: RegisterData): Promise<boolean> => {
        set({ isLoading: true });
        try {
          const response = await fetch(`${API_URL}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
          });

          if (!response.ok) {
            set({ isLoading: false });
            return false;
          }

          const result = await response.json();
          set({
            token: result.token,
            user: result.user,
            workspace: result.workspace,
            availableWorkspaces: [result.workspace],
            isAuthenticated: true,
            isLoading: false,
          });
          return true;
        } catch (error) {
          console.error('Register error:', error);
          set({ isLoading: false });
          return false;
        }
      },

      switchWorkspace: async (workspaceSlug: string): Promise<boolean> => {
        const { token } = get();
        if (!token) return false;

        set({ isLoading: true });
        try {
          const response = await fetch(`${API_URL}/auth/switch-workspace`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ workspaceSlug }),
          });

          if (!response.ok) {
            set({ isLoading: false });
            return false;
          }

          const data = await response.json();
          set({
            token: data.token,
            workspace: data.workspace,
            isLoading: false,
          });
          return true;
        } catch (error) {
          console.error('Switch workspace error:', error);
          set({ isLoading: false });
          return false;
        }
      },

      handleOAuthCallback: (token: string, data?: OAuthCallbackData) => {
        if (data) {
          // Popup flow with full data
          set({
            token,
            user: data.user ? {
              id: data.user.id,
              email: data.user.email,
              firstName: data.user.firstName,
              lastName: data.user.lastName,
              avatarUrl: data.user.avatarUrl,
            } : null,
            workspace: data.workspace || null,
            availableWorkspaces: data.workspaces || [],
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          // Redirect flow - token only, need to fetch user data
          // For now, just set the token
          set({
            token,
            isAuthenticated: true,
            isLoading: false,
          });
          
          // In a real implementation, you'd fetch user/workspace data here
        }
      },

      updateWorkspace: (updates: Partial<Workspace>) => {
        const { workspace } = get();
        set({ workspace: { ...(workspace || {} as Workspace), ...updates } });
      },

      logout: () => {
        set({
          token: null,
          user: null,
          workspace: null,
          availableWorkspaces: [],
          isAuthenticated: false,
        });
      },

      getAuthHeaders: () => {
        const { token } = get();
        return token ? { Authorization: `Bearer ${token}` } : {};
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        workspace: state.workspace,
        availableWorkspaces: state.availableWorkspaces,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
